import React from 'react';
import { useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  StyledList,
  EnhancedGoBackButton
} from '../../components';
import { JOB_DETAILS } from '../../data';
import { getJobById, isInternship } from '../../data';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const JobDetailsPage = ({ setRefs }) => {
  const { jobId } = useParams();

  const notFoundRef = useRef(null);
  const jobTitleRef = useRef(null);
  const responsibilitiesRef = useRef(null);
  const requirementsRef = useRef(null);
  const requirementsListRef = useRef(null);
  const offerRef = useRef(null);
  const offerTextRef = useRef(null);
  const confidentialityRef = useRef(null);
  const footerTextRef = useRef(null);
  const footerRef = useRef(null);

  const refs = useMemo(
    () => ({
      notFound: notFoundRef,
      jobTitle: jobTitleRef,
      responsibilities: responsibilitiesRef,
      requirements: requirementsRef,
      requirementsList: requirementsListRef,
      offer: offerRef,
      offerText: offerTextRef,
      confidentiality: confidentialityRef,
      footerText: footerTextRef,
      footer: footerRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  // Get job data
  const job = getJobById(jobId);

  // Handle job not found
  if (!job) {
    return (
      <>
        <Box sx={{ mb: SECTION_SPACING, mt: SECTION_SPACING }}>
          <EnhancedGoBackButton
            backTo={ROUTES.HOW_TO_JOIN.ROOT}
            backText='Back to How to Join'
          />
        </Box>
        <SectionContent
          id='notFound'
          ref={refs.notFound}
          title='Job Not Found'
          titleVariant='TitleLarge'
          titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
          text={[
            {
              text: "We couldn't find the job listing you're looking for. It might have been removed or the URL might be incorrect.",
              marginBottom: ELEMENT_SPACING,
              fontSize: '1.3rem'
            }
          ]}
        />
      </>
    );
  }

  // Determine if this is a job or internship
  const isJobInternship = isInternship(jobId);

  // Convert text items to list format for StyledList
  const styledListItems = job.details[1].text
    .split('\n')
    .filter(sentence => sentence.trim() !== '')
    .map(sentence => ({
      text: sentence.trim()
    }));

  return (
    <>
      <SEO
        title={
          job
            ? `${job.title} - ${job.location} | Careers at MarQ Capital`
            : seoData.howToJoin.title
        }
        content={
          job
            ? `Apply for the ${job.title} position at MarQ Capital in ${job.location}. ${job.details?.[0]?.text?.substring?.(0, 150) || ''}`
            : seoData.howToJoin.content
        }
        keywords={seoData.howToJoin.keywords}
        canonical={`/how-to-join/${job?.id || ''}`}
        ogImage={seoData.howToJoin.ogImage}
      />
      {/* Go Back Button */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <EnhancedGoBackButton
          backTo={
            isJobInternship
              ? ROUTES.HOW_TO_JOIN.INTERNSHIP
              : ROUTES.HOW_TO_JOIN.ROOT + '#chooseYourPath'
          }
          backText={`View all ${isJobInternship ? 'Internships' : 'Jobs'}`}
        />
      </Box>

      {/* Job Title and Description */}
      <SectionContent
        id='jobTitle'
        ref={refs.jobTitle}
        title={`${job.location} | ${job.title}`}
        titleVariant='TitleLarge'
        titleMargin={`0 0 ${SECTION_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: job.description,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* First Detail - Day-to-day responsibilities */}
      <Box
        id='responsibilities'
        ref={refs.responsibilities}
        sx={{
          backgroundColor: THEME.COLORS.NEUTRAL_BLACK,
          padding: ELEMENT_SPACING,
          marginBottom: SECTION_SPACING,
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: THEME.COLORS.PRIMARY_BLUE
          }
        }}
      >
        <Typography
          variant='h2'
          sx={{
            fontSize: { xs: '1.3rem', sm: '1.3rem', md: '1.3rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
            margin: `${ELEMENT_SPACING} 0`,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            textAlign: 'center',
            color: THEME.COLORS.NEUTRAL_WHITE
          }}
        >
          {job.details[0].title}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1.3rem',
            lineHeight: 1.6,
            color: THEME.COLORS.NEUTRAL_WHITE,
            textAlign: 'center',
            padding: `${ELEMENT_SPACING} ${ELEMENT_SPACING * 3}`,
            marginBottom: ELEMENT_SPACING
          }}
        >
          {job.details[0].text}
        </Typography>
      </Box>

      {/* Second Detail - Who we're looking for */}
      <SectionContent
        id='requirements'
        ref={refs.requirements}
        title={job.details[1].title}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
      />

      <Box
        ref={refs.requirementsList}
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING * 3,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList variant='blue' items={styledListItems} />
      </Box>

      {/* Third Detail - What we offer */}
      <SectionContent
        id='offer'
        ref={refs.offer}
        title={job.details[2].title}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
      />

      <SectionContent
        id='offerText'
        ref={refs.offerText}
        initialSpacing={false}
        text={[
          {
            text: job.details[2].text,
            textAlign: 'center',
            padding: `${ELEMENT_SPACING}`,
            fontSize: '1.3rem',
            backgroundColor: THEME.COLORS.NEUTRAL_BLACK,
            color: THEME.COLORS.NEUTRAL_WHITE,
            hoverColor: THEME.COLORS.PRIMARY_BLUE,
            marginBottom: SECTION_SPACING
          }
        ]}
      />

      {/* Confidentiality and Data Retention */}
      <SectionContent
        id='confidentiality'
        ref={refs.confidentiality}
        title={JOB_DETAILS.CONFIDENTIALITY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={[
          {
            word:
              job.company === 'MarQ Capital'
                ? 'recruiting@mqcapital.co.uk'
                : 'recruiting@spinnakeralgo.com',
            url: `mailto:${job.company === 'MarQ Capital' ? 'recruiting@mqcapital.co.uk' : 'recruiting@spinnakeralgo.com'}`,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={JOB_DETAILS.CONFIDENTIALITY.TEXT(job.company).map(
          (text, index, array) => ({
            text,
            marginBottom:
              index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
            fontSize: '1.1rem'
          })
        )}
      />

      {/* Footer note with separator line */}
      <Box
        ref={refs.footer}
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      ></Box>

      {/* Footer text section */}
      <SectionContent
        id='footerText'
        ref={refs.footerText}
        initialSpacing={false}
        text={[
          {
            text: 'This job posting was last updated on April 10, 2025. Applications will be considered on a rolling basis.',
            fontStyle: 'italic',
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY
          }
        ]}
      />
    </>
  );
};

export default JobDetailsPage;
